import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Upload,
  FileSpreadsheet,
  Building2,
  Layers,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Download,
  Eye,
  X,
  RefreshCw,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const POLL_INTERVAL = 2000; // 2 seconds

const V2CsvUpload = () => {
  const { getAccessToken } = useAuth();
  const [activeTab, setActiveTab] = useState("providers");
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [orgId, setOrgId] = useState("");
  const [jobStatus, setJobStatus] = useState(null);
  const [jobId, setJobId] = useState(null);
  const pollIntervalRef = useRef(null);

  const tabs = [
    { id: "providers", label: "Providers", icon: Building2 },
    { id: "programs", label: "Programs", icon: Layers },
    { id: "sessions", label: "Sessions", icon: Calendar },
  ];

  // Parse CSV preview (first 5 rows)
  const parsePreview = useCallback((file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split("\n").filter((line) => line.trim());

        if (lines.length === 0) {
          reject(new Error("File is empty"));
          return;
        }

        // Parse headers
        const headers = lines[0].split(",").map((h) => h.trim());

        // Parse first 5 data rows
        const rows = [];
        for (let i = 1; i < Math.min(lines.length, 6); i++) {
          const values = lines[i].split(",").map((v) => v.trim());
          const row = {};
          headers.forEach((header, index) => {
            row[header] = values[index] || "";
          });
          rows.push(row);
        }

        resolve({ headers, rows, totalRows: lines.length - 1 });
      };
      reader.onerror = reject;
      reader.readAsText(file);
    });
  }, []);

  const handleFileSelect = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setError(null);
      setPreview(null);
      setUploadResult(null);

      // Validate file size
      if (file.size > MAX_FILE_SIZE) {
        setError("File size exceeds 5MB limit");
        return;
      }

      // Validate file type
      if (!file.name.endsWith(".csv")) {
        setError("Only CSV files are allowed");
        return;
      }

      try {
        const previewData = await parsePreview(file);
        setSelectedFile(file);
        setPreview(previewData);
      } catch (err) {
        setError(`Failed to parse CSV file: ${err.message}`);
      }
    },
    [parsePreview]
  );

  // Poll for job status
  const pollJobStatus = useCallback(async (id) => {
    try {
      const token = getAccessToken();
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://api.littleexplorers.help'}/uploads/${id}`,
        { headers }
      );

      if (!response.ok) {
        throw new Error("Failed to fetch job status");
      }

      const job = await response.json();
      setJobStatus(job);

      // Stop polling if job is complete
      if (["completed", "failed", "cancelled"].includes(job.status)) {
        if (pollIntervalRef.current) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
        }
        setIsUploading(false);
        setUploadResult(job);
        setSelectedFile(null);
        setPreview(null);
      }
    } catch (err) {
      console.error("Error polling job status:", err);
    }
  }, [getAccessToken]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) return;

    try {
      setIsUploading(true);
      setError(null);
      setJobStatus(null);
      setUploadResult(null);

      const formData = new FormData();
      formData.append("csvFile", selectedFile);
      formData.append("type", activeTab);

      // Add orgId for programs import
      if (activeTab === "programs" && orgId) {
        formData.append("orgId", orgId);
      }

      const token = getAccessToken();
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://api.littleexplorers.help'}/uploads/csv`,
        {
          method: "POST",
          headers,
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Upload failed");
      }

      setJobId(result.jobId);
      
      // Start polling for job status
      pollJobStatus(result.jobId);
      pollIntervalRef.current = setInterval(() => {
        pollJobStatus(result.jobId);
      }, POLL_INTERVAL);
    } catch (err) {
      setError(err.message);
      setIsUploading(false);
    }
  }, [selectedFile, activeTab, orgId, getAccessToken, pollJobStatus]);

  const handleCancel = useCallback(async () => {
    if (!jobId) return;

    try {
      const token = getAccessToken();
      const headers = {};
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'https://api.littleexplorers.help'}/uploads/${jobId}/cancel`,
        {
          method: "POST",
          headers,
        }
      );

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.error || "Failed to cancel job");
      }

      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }

      setIsUploading(false);
    } catch (err) {
      setError(err.message);
    }
  }, [jobId, getAccessToken]);

  const handleClearPreview = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    setUploadResult(null);
    setOrgId("");
  }, []);

  const downloadTemplate = (type) => {
    let csvContent = "";
    let filename = "";

    switch (type) {
      case "providers":
        csvContent =
          "provider_name,type,website_url,description,provider_scope,base_city,base_state,contact_phone,contact_email,is_verified,is_active\n";
        csvContent +=
          'MPSA Crush Soccer,Nonprofit,https://mpsacrush.com,Soccer & futsal programs,Local,Ann Arbor,MI,(734) 249-9193,info@mpsacrush.com,true,true\n';
        filename = "providers_template.csv";
        break;
      case "programs":
        csvContent =
          "provider_id,program_name,description,category,subcategory,skill_level,min_age,max_age,session_type,indoor_or_outdoor,trial_class_available,drop_in_available,sibling_discount,scholarship_available,registration_url,requires_inquiry,inquiry_url\n";
        csvContent +=
          'PROV-001,Future Stars Soccer,Soccer for beginners,Sports,Soccer,Beginner,4,6,In-Person,Indoor,false,false,true,true,https://mpsacrush.com/register,false,\n';
        csvContent +=
          'PROV-001,Elite Training,Advanced training program,Sports,Soccer,Advanced,10,14,In-Person,Outdoor,false,false,false,true,,true,https://mpsacrush.com/inquiry\n';
        filename = "programs_template.csv";
        break;
      case "sessions":
        csvContent =
          "program_id,session_name,description,start_date,end_date,days_of_week,start_time,end_time,capacity,price_total,registration_open_date,registration_close_date,registration_url,is_tryout\n";
        csvContent +=
          'PROG-001,Spring Session 2024,Spring soccer program for ages 4-6,2024-03-15,2024-04-19,Mon/Wed,16:00,17:00,20,150.00,2024-02-01,2024-03-10,,false\n';
        csvContent +=
          'PROG-001,Spring Tryouts 2024,Evaluation session for team placement,2024-03-01,2024-03-02,Sat/Sun,10:00,12:00,50,0.00,2024-02-01,2024-02-28,https://mpsacrush.com/tryouts,true\n';
        filename = "sessions_template.csv";
        break;
    }

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const currentTab = tabs.find((t) => t.id === activeTab);
  const Icon = currentTab?.icon || Building2;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white">CSV Upload (V2)</h1>
        <p className="text-gray-400 mt-1">
          Bulk upload providers, programs, and sessions for the new 3-tier structure
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-4 border-b border-gray-700">
        {tabs.map((tab) => {
          const TabIcon = tab.icon;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={`pb-3 px-4 font-medium transition-colors ${
                activeTab === tab.id
                  ? "text-indigo-400 border-b-2 border-indigo-400"
                  : "text-gray-400 hover:text-white"
              }`}
            >
              <div className="flex items-center gap-2">
                <TabIcon size={18} />
                {tab.label}
              </div>
            </button>
          );
        })}
      </div>

      {/* Upload Result */}
      {uploadResult && (
        <div
          className={`p-4 rounded-lg ${
            uploadResult.status === "completed"
              ? "bg-green-500/10 border border-green-500/20"
              : uploadResult.status === "failed"
              ? "bg-red-500/10 border border-red-500/20"
              : "bg-yellow-500/10 border border-yellow-500/20"
          }`}
        >
          <div className="flex items-center gap-2 mb-2">
            {uploadResult.status === "completed" ? (
              <CheckCircle className="w-5 h-5 text-green-400" />
            ) : uploadResult.status === "failed" ? (
              <XCircle className="w-5 h-5 text-red-400" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-400" />
            )}
            <span
              className={`font-medium ${
                uploadResult.status === "completed"
                  ? "text-green-400"
                  : uploadResult.status === "failed"
                  ? "text-red-400"
                  : "text-yellow-400"
              }`}
            >
              {uploadResult.status === "completed"
                ? "Upload Complete"
                : uploadResult.status === "failed"
                ? "Upload Failed"
                : uploadResult.status === "cancelled"
                ? "Upload Cancelled"
                : "Upload Finished"}
            </span>
          </div>
          <p className="text-gray-400 text-sm">
            Successful: {uploadResult.successfulRows || 0} | Failed: {uploadResult.failedRows || 0}
            {uploadResult.totalRows > 0 && ` | Total: ${uploadResult.totalRows}`}
          </p>
          {uploadResult.processingErrors && uploadResult.processingErrors.length > 0 && (
            <div className="mt-3 max-h-40 overflow-y-auto">
              <p className="text-red-400 text-sm mb-2">Errors:</p>
              {uploadResult.processingErrors.slice(0, 10).map((err, idx) => (
                <p key={idx} className="text-gray-400 text-xs">
                  Row {err.row}: {err.error}
                </p>
              ))}
              {uploadResult.processingErrors.length > 10 && (
                <p className="text-gray-500 text-xs">
                  ... and {uploadResult.processingErrors.length - 10} more
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upload Form */}
      <div className="glass-card p-6">
        {/* Org ID input for programs import */}
        {activeTab === "programs" && (
          <div className="mb-6">
            <label className="block text-white mb-2 flex items-center gap-2">
              <Building2 size={18} />
              Organization/Provider ID *
            </label>
            <input
              type="text"
              value={orgId}
              onChange={(e) => setOrgId(e.target.value)}
              placeholder="Enter the provider ID (e.g., org_abc123)"
              className="input-field w-full text-black px-2"
              required
            />
            <p className="text-gray-500 text-sm mt-1">
              The provider/organization ID that all programs will be associated with
            </p>
          </div>
        )}

        {/* File Upload */}
        <div className="mb-6">
          <label className="block text-white mb-2 flex items-center gap-2">
            <Icon size={18} />
            {currentTab?.label} CSV File *
          </label>
          <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center hover:border-indigo-500 transition-colors">
            <input
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              id="csv-upload"
            />
            <label htmlFor="csv-upload" className="cursor-pointer">
              <Upload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-white mb-2">
                {selectedFile ? selectedFile.name : "Click to select CSV file"}
              </p>
              <p className="text-gray-500 text-sm">Max file size: 5MB</p>
            </label>
          </div>
        </div>

        {/* Preview */}
        {preview && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white font-medium flex items-center gap-2">
                <Eye size={18} />
                Preview (first {preview.rows.length} of {preview.totalRows} rows)
              </h3>
              <button
                type="button"
                onClick={handleClearPreview}
                className="text-gray-400 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    {preview.headers.map((header) => (
                      <th
                        key={header}
                        className="text-left py-2 px-3 text-gray-400"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.rows.map((row, rowIdx) => (
                    <tr
                      key={`preview-row-${rowIdx}`}
                      className="border-b border-gray-800"
                    >
                      {preview.headers.map((header, colIdx) => (
                        <td
                          key={`preview-cell-${rowIdx}-${colIdx}`}
                          className="py-2 px-3 text-gray-300"
                        >
                          {row[header]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Job Status / Progress */}
        {jobStatus && isUploading && (
          <div className="mb-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-blue-400 font-medium flex items-center gap-2">
                <RefreshCw size={18} className="animate-spin" />
                Processing {jobStatus.type} import...
              </span>
              <span className="text-gray-400 text-sm">
                Status: {jobStatus.status}
              </span>
            </div>
            {jobStatus.totalRows > 0 && (
              <div className="mt-2">
                <div className="flex justify-between text-sm text-gray-400 mb-1">
                  <span>Progress</span>
                  <span>
                    {jobStatus.processedRows} / {jobStatus.totalRows} rows
                  </span>
                </div>
                <div className="w-full bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${Math.round((jobStatus.processedRows / jobStatus.totalRows) * 100)}%`,
                    }}
                  />
                </div>
                <div className="flex gap-4 mt-2 text-sm">
                  <span className="text-green-400">
                    ✓ {jobStatus.successfulRows} successful
                  </span>
                  {jobStatus.failedRows > 0 && (
                    <span className="text-red-400">
                      ✗ {jobStatus.failedRows} failed
                    </span>
                  )}
                </div>
              </div>
            )}
            <div className="mt-3">
              <button
                type="button"
                onClick={handleCancel}
                disabled={jobStatus.status === "cancelled"}
                className="text-red-400 hover:text-red-300 text-sm flex items-center gap-2 disabled:opacity-50"
              >
                <X size={16} />
                Cancel Upload
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => downloadTemplate(activeTab)}
            className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center gap-2"
          >
            <Download size={16} />
            Download {currentTab?.label} Template
          </button>

          <button
            type="button"
            onClick={handleUpload}
            disabled={!selectedFile || isUploading}
            className="btn-futuristic disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Uploading...
              </>
            ) : (
              "Start Upload"
            )}
          </button>
        </div>
      </div>

      {/* Field Reference */}
      <div className="glass-card p-6">
        <h3 className="text-white font-medium mb-4">Required Fields Reference</h3>
        <div className="text-sm text-gray-400 space-y-2">
          {activeTab === "providers" && (
            <ul className="list-disc list-inside space-y-1">
              <li><strong>provider_name</strong> - Organization name</li>
              <li><strong>type</strong> - Organization type (e.g., Nonprofit, For-profit)</li>
              <li><strong>base_city</strong> - City location</li>
              <li><strong>base_state</strong> - State (e.g., MI)</li>
            </ul>
          )}
          {activeTab === "programs" && (
            <ul className="list-disc list-inside space-y-1">
              <li><strong>provider_id</strong> - Reference to provider</li>
              <li><strong>program_name</strong> - Program name</li>
              <li><strong>category</strong> - Activity category (e.g., Sports)</li>
              <li><strong>min_age</strong> - Minimum age (number)</li>
              <li><strong>max_age</strong> - Maximum age (number)</li>
              <li><strong>registration_url</strong> - Optional program-level registration link</li>
              <li><strong>requires_inquiry</strong> - true if program requires form submission (no sessions)</li>
              <li><strong>inquiry_url</strong> - URL for inquiry form (required if requires_inquiry is true)</li>
            </ul>
          )}
          {activeTab === "sessions" && (
            <ul className="list-disc list-inside space-y-1">
              <li><strong>program_id</strong> - Reference to program</li>
              <li><strong>session_name</strong> - Name of the session</li>
              <li><strong>description</strong> - Optional session description</li>
              <li><strong>start_date</strong> - Start date (YYYY-MM-DD)</li>
              <li><strong>end_date</strong> - End date (YYYY-MM-DD)</li>
              <li><strong>days_of_week</strong> - Days (e.g., Mon/Wed/Fri)</li>
              <li><strong>registration_url</strong> - Optional session-specific registration link</li>
              <li><strong>is_tryout</strong> - true if this is a tryout/evaluation session</li>
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default V2CsvUpload;
